import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Check, Loader2, ShieldCheck, Users, Sparkles, FileText, Gift } from "lucide-react";
import { toast } from "sonner";
import PublicShell, { WIDE } from "@/components/PublicShell";
import { PhoneFrame, SampleScreen, SynastrySnapshot, SynastryCharts, SynastrySections, SAMPLE_SYNASTRY } from "@/components/SynastryView";
import { BirthDatePicker, BirthTimePicker } from "@/components/BirthDateTime";
import { READING_BY_SLUG, PEW, PRIVACY_POINTS } from "@/lib/readingsContent";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { isNative } from "@/lib/native";
import { api } from "@/lib/apiClient";
import { usePromo, PromoCountdown } from "@/lib/promo";
import ShareOfferTab from "@/components/ShareOfferTab";

const wrap = WIDE ? "lg:max-w-6xl lg:mx-auto lg:px-10" : "";
const input = "w-full bg-[#04060B] border border-[#D4AF37]/20 rounded-xl px-3 py-2.5 text-base text-starlight placeholder:text-[#5f6b7d] focus:outline-none focus:border-[#D4AF37]/60";
const errMsg = (e) => e.response?.data?.detail || "Something went wrong. Please try again.";

function Block({ kicker, title, children, testId }) {
  return (
    <section data-testid={testId} className={`px-5 mt-12 ${wrap} ${WIDE ? "lg:mt-20" : ""}`}>
      <p className="text-[11px] uppercase tracking-[0.3em] gold-text">{kicker}</p>
      <h2 className={`text-3xl font-light mt-1 mb-5 ${WIDE ? "lg:text-4xl lg:mb-8" : ""}`}>{title}</h2>
      {children}
    </section>
  );
}

function Samples({ r }) {
  const frames = r.slug === "synastry" ? [
    <PhoneFrame key="a" testId="sample-screen-1"><div className="px-4 pt-2 scale-[0.92] origin-top"><SynastrySnapshot report={SAMPLE_SYNASTRY} /></div></PhoneFrame>,
    <PhoneFrame key="b" testId="sample-screen-2"><div className="px-3 pt-2 scale-[0.9] origin-top"><SynastryCharts report={SAMPLE_SYNASTRY} /></div></PhoneFrame>,
    <PhoneFrame key="c" testId="sample-screen-3"><div className="px-3 pt-2 scale-[0.9] origin-top"><SynastrySections report={SAMPLE_SYNASTRY} limit={2} /></div></PhoneFrame>,
  ] : [
    <PhoneFrame key="a" testId="sample-screen-1"><SampleScreen sample={r.sample} /></PhoneFrame>,
    <PhoneFrame key="b" testId="sample-screen-2">
      <div className="p-5">
        <p className="text-[10px] uppercase tracking-[0.25em] gold-text">Inside your report</p>
        <h3 className="text-2xl font-light mt-1 mb-4">{r.title}</h3>
        <ol className="space-y-3">
          {r.includes.map((x, i) => (
            <li key={x} className="flex gap-3 text-sm text-[#D6D0C5]"><span className="gold-text">{String(i + 1).padStart(2, "0")}</span>{x}</li>
          ))}
        </ol>
      </div>
    </PhoneFrame>,
  ];
  return (
    <div className={`flex gap-5 overflow-x-auto pb-4 -mx-5 px-5 snap-x ${WIDE ? "lg:mx-0 lg:px-0 lg:justify-start lg:gap-8 lg:overflow-visible" : ""}`}>
      {frames.map((f, i) => <div key={i} className="snap-center">{f}</div>)}
    </div>
  );
}

function Stats({ r }) {
  const syn = r.slug === "synastry";
  return (
    <Block kicker="Who turns to the stars" testId="reading-stats"
      title={syn ? "Why so many women rely on synastry" : `How many people use readings like this`}>
      {syn && (
        <div className={`text-base text-[#D6D0C5] leading-relaxed space-y-3 mb-6 ${WIDE ? "lg:text-lg lg:max-w-3xl" : ""}`}>
          <p>Women are almost twice as likely as men to consult astrology, and relationships are the most common reason they do. Synastry speaks directly to the questions people care about most: will this last, why do we clash, and how can we understand each other better?</p>
          <p>Rather than a yes-or-no verdict, it offers a shared language for feelings that are hard to put into words, which is why many couples use it to plan big steps, from moving in together to choosing a wedding date.</p>
        </div>
      )}
      <div className={`grid gap-3 ${WIDE ? "lg:grid-cols-3 lg:gap-5" : ""}`}>
        {PEW.stats.map((s) => (
          <div key={s.value} className="bg-[#0F1523]/90 border border-[#D4AF37]/20 rounded-2xl p-5" data-testid="reading-stat">
            <p className="text-5xl font-light gold-glow-text">{s.value}</p>
            <p className="text-sm text-[#D6D0C5] mt-2">{s.label}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-[#A7B0C0] mt-4 max-w-3xl">{PEW.note} Source: <a href={PEW.url} target="_blank" rel="noreferrer" className="gold-text underline">{PEW.source}</a>.</p>
    </Block>
  );
}

function Privacy() {
  return (
    <Block kicker="Your details are safe" title="Why we ask for your birth details" testId="reading-privacy">
      <ul className={`grid gap-3 ${WIDE ? "lg:grid-cols-2 lg:gap-5" : ""}`}>
        {PRIVACY_POINTS.map((p) => (
          <li key={p} className="flex gap-3 bg-[#0F1523]/90 border border-[#D4AF37]/15 rounded-2xl p-4">
            <ShieldCheck size={18} className="gold-text shrink-0 mt-0.5" /><span className="text-base text-[#D6D0C5]">{p}</span>
          </li>
        ))}
      </ul>
    </Block>
  );
}

function PartnerFields({ partner, setPartner, prefix = "partner" }) {
  const set = (k) => (v) => setPartner({ ...partner, [k]: v });
  return (
    <div className="space-y-2.5">
      <input data-testid={`${prefix}-name`} className={input} placeholder={prefix === "you" ? "Your first name" : "Their first name"} value={partner.name} onChange={(e) => set("name")(e.target.value)} />
      <div><label className="text-xs text-[#A7B0C0] ml-1 block mb-1">Date of birth</label><BirthDatePicker testId={`${prefix}-dob`} value={partner.birth_date} onChange={set("birth_date")} /></div>
      <div><label className="text-xs text-[#A7B0C0] ml-1 block mb-1">Time of birth (optional, makes it more accurate)</label><BirthTimePicker testId={`${prefix}-tob`} value={partner.birth_time} onChange={set("birth_time")} /></div>
      <input data-testid={`${prefix}-pob`} className={input} placeholder="City of birth (optional)" value={partner.birth_place} onChange={(e) => set("birth_place")(e.target.value)} />
    </div>
  );
}

const EMPTY = { name: "", birth_date: "", birth_time: "", birth_place: "" };
const GIFTABLE = ["natal", "transit", "solar-return", "progression", "astrocartography", "sun-sign-deep"];

function GiftLink({ slug }) {
  return (
    <Link to={`/gifts?slug=${slug}`} data-testid="link-gift-reading" className="mt-4 inline-flex items-center gap-2 text-sm gold-text hover:underline underline-offset-4">
      <Gift size={15} /> Or gift this reading to a friend on a date you choose
    </Link>
  );
}

function BuyBlock({ r }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { currency, prices } = useCurrency();
  const [question, setQuestion] = useState("");
  const [partner, setPartner] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const price = `${prices.symbol}${prices.reading}`;
  const ready = r.needs === "question" ? question.trim().length > 3 : r.needs === "partner" ? partner.name && partner.birth_date : true;
  const buy = async () => {
    if (!user) return navigate(`/join?next=${encodeURIComponent(`/readings/${r.slug}`)}`);
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "reading_single", reading_type: r.slug, currency, origin_url: window.location.origin,
        question: r.needs === "question" ? question : "", partner: r.needs === "partner" ? partner : undefined });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="bg-gradient-to-b from-[#1a1230] to-[#0F1523] border border-[#D4AF37]/40 rounded-3xl p-6 lg:p-10" data-testid="reading-buy">
      <p className="text-4xl font-light gold-glow-text" data-testid="reading-price">{price}</p>
      <p className="text-sm text-[#A7B0C0] mb-5">One-off payment · yours to keep in your archive</p>
      {user && r.needs === "question" && (
        <textarea data-testid="reading-question" rows={3} value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={500}
          placeholder={r.slug === "horary" ? "Your question, e.g. Should I accept the job offer?" : "The event and rough dates, e.g. our wedding, next spring"} className={`${input} mb-4`} />
      )}
      {user && r.needs === "partner" && <div className="mb-4"><PartnerFields partner={partner} setPartner={setPartner} /></div>}
      <button data-testid="btn-get-reading" onClick={buy} disabled={busy || (user && !ready)}
        className="w-full lg:w-auto lg:px-12 flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="animate-spin" size={18} /> : `Get Your ${r.title} Today · ${price}`}
      </button>
      {!user && <p className="text-xs text-[#A7B0C0] mt-3">You'll create a free account with your birth details first, so the reading is written for your chart.</p>}
      {user && !user.is_demo && GIFTABLE.includes(r.slug) && <GiftLink slug={r.slug} />}
    </div>
  );
}

function OfferPrice({ promo, premium, testId }) {
  const { prices } = useCurrency();
  const price = `${prices.symbol}${prices.reading}`;
  if (!promo) return (
    <>
      <p className="text-4xl font-light gold-glow-text" data-testid={testId}>{price}</p>
      <p className="text-sm text-[#A7B0C0] mt-1 mb-6">One-off payment · yours to keep in your archive</p>
    </>
  );
  return (
    <>
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] bg-[#D4AF37] text-[#04060B] px-3 py-1 rounded-full mb-4"><Sparkles size={11} /> Limited-time offer</span>
      <p className="text-4xl font-light" data-testid={testId}>
        <s className="text-[#6c7686] decoration-[#D4AF37]/70">{price}</s> <span className="gold-glow-text ml-2">FREE</span>
      </p>
      <PromoCountdown endsAt={promo.ends_at} className="mt-3" />
      <p className="text-sm text-[#A7B0C0] mt-2 mb-6">
        {premium ? "Included in your Premium membership." : `When you join Astra Premium, ${prices.symbol}${prices.monthly}/month, cancel any time. Your reading appears as soon as you've joined.`}
      </p>
    </>
  );
}

function AccountFields({ me, setMe, acct, setAcct }) {
  return (
    <div className="space-y-2.5" data-testid="synastry-offer-you">
      <p className="text-lg"><Users size={16} className="inline gold-text mr-2 -mt-1" />You</p>
      <PartnerFields partner={me} setPartner={setMe} prefix="you" />
      <input data-testid="you-email" type="email" className={input} placeholder="Email" value={acct.email} onChange={(e) => setAcct({ ...acct, email: e.target.value })} />
      <input data-testid="you-password" type="password" className={input} placeholder="Choose a password (6+ characters)" value={acct.password} onChange={(e) => setAcct({ ...acct, password: e.target.value })} />
      <p className="text-xs text-[#A7B0C0]">This creates your Astra account at the same time.</p>
    </div>
  );
}

const tz = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

function PromoOffer({ promo, r }) {
  const key = r.slug === "natal" ? "natal" : `promo-${r.slug}`;
  const claimed = user => (user?.promo_claimed || []).includes(promo.id);
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const { currency } = useCurrency();
  const [me, setMe] = useState(EMPTY);
  const [acct, setAcct] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const ready = user || (me.name && me.birth_date && acct.email && acct.password.length >= 6);
  const go = async () => {
    setBusy(true);
    try {
      if (!user) await register({ ...me, ...acct, timezone: tz() });
      const { data } = await api.post(`/promo/${r.slug}/claim`, { currency, origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else { toast.success(`Your ${r.title} will be in your Archive in about a minute`); navigate("/history?promo=1"); }
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="bg-gradient-to-b from-[#1a1230] to-[#0F1523] border border-[#D4AF37]/50 rounded-3xl p-6 lg:p-10" data-testid={`${key}-offer`}>
      <OfferPrice promo={promo} premium={user?.is_premium} testId={`${key}-offer-price`} />
      {!user && <div className={WIDE ? "lg:max-w-md" : ""}><AccountFields me={me} setMe={setMe} acct={acct} setAcct={setAcct} /></div>}
      <p className="text-xs text-[#A7B0C0] mt-5 flex gap-2"><ShieldCheck size={14} className="gold-text shrink-0" />Birth details are used only to calculate your chart accurately and stay private in your account.</p>
      <button data-testid={`btn-get-${key}`} onClick={go} disabled={busy || !ready || claimed(user)}
        className="w-full lg:w-auto lg:px-12 mt-5 flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="animate-spin" size={18} /> : claimed(user) ? "Already claimed, see your Archive" : `Get Your ${r.title} Today, FREE`}
      </button>
      {!user && <p className="text-sm text-[#A7B0C0] mt-4">Already have an account? <Link to={`/join?mode=login&next=${encodeURIComponent(`/readings/${r.slug}`)}`} className="gold-text" data-testid={`${key}-offer-signin`}>Sign in</Link></p>}
    </div>
  );
}

function SynastryOffer({ promo }) {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const { currency, prices } = useCurrency();
  const [me, setMe] = useState(EMPTY);
  const [acct, setAcct] = useState({ email: "", password: "" });
  const [partner, setPartner] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const premium = user?.is_premium;
  const ready = partner.name && partner.birth_date && (user || (me.name && me.birth_date && acct.email && acct.password.length >= 6));
  const go = async () => {
    setBusy(true);
    try {
      if (!user) await register({ ...me, ...acct, timezone: tz() });
      const { data } = await api.post("/synastry/start", { partner, currency, origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else navigate(`/synastry/${data.report_id}`);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="bg-gradient-to-b from-[#1a1230] to-[#0F1523] border border-[#D4AF37]/50 rounded-3xl p-6 lg:p-10" data-testid="synastry-offer">
      <OfferPrice promo={promo} premium={premium} testId="synastry-offer-price" />
      <div className={`grid gap-6 ${WIDE ? "lg:grid-cols-2 lg:gap-10" : ""}`}>
        {!user && <AccountFields me={me} setMe={setMe} acct={acct} setAcct={setAcct} />}
        <div className="space-y-2.5" data-testid="synastry-offer-partner">
          <p className="text-lg"><Users size={16} className="inline gold-text mr-2 -mt-1" />Your partner</p>
          <PartnerFields partner={partner} setPartner={setPartner} />
        </div>
      </div>
      <p className="text-xs text-[#A7B0C0] mt-5 flex gap-2"><ShieldCheck size={14} className="gold-text shrink-0" />Birth details are used only to calculate both charts accurately and stay private in your account.</p>
      <button data-testid="btn-get-synastry" onClick={go} disabled={busy || !ready}
        className="w-full lg:w-auto lg:px-12 mt-5 flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="animate-spin" size={18} /> : !promo ? `Get Your Synastry Reading Today · ${prices.symbol}${prices.reading}` : premium ? "Create my Synastry Reading" : "Get Your Synastry Reading Today, FREE"}
      </button>
      {!user && <p className="text-sm text-[#A7B0C0] mt-4">Already have an account? <Link to={`/join?mode=login&next=${encodeURIComponent("/readings/synastry")}`} className="gold-text" data-testid="synastry-offer-signin">Sign in</Link></p>}
      <p className="text-sm text-[#A7B0C0] mt-6 flex gap-2"><FileText size={15} className="gold-text shrink-0 mt-0.5" />Want to keep it? A longer illustrated PDF with both birth wheels and five extra chapters is available for {prices.symbol}{prices.reading}.</p>
    </div>
  );
}

export default function ReadingDetail() {
  const { slug } = useParams();
  const r = READING_BY_SLUG[slug];
  const promo = usePromo();
  const onPromo = !isNative && promo?.slug === slug ? promo : null;
  useEffect(() => { if (r) document.title = `${r.title} · Astra`; window.scrollTo(0, 0); }, [r]);
  if (!r) return <Navigate to="/readings" replace />;
  return (
    <PublicShell testId={`reading-page-${slug}`}>
      {onPromo && <ShareOfferTab slug={slug} />}
      <div className={`px-5 pt-4 fade-up ${wrap} ${WIDE ? "lg:pt-10 lg:grid lg:grid-cols-[1fr_auto] lg:gap-14 lg:items-center" : ""}`}>
        <div>
          <Link to="/readings" data-testid="link-all-readings" className="text-sm text-[#A7B0C0] hover:text-starlight">← All readings</Link>
          <p className="text-[11px] uppercase tracking-[0.3em] gold-text mt-4">{r.tag}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light mt-1 mb-4" data-testid="reading-title">{r.title}</h1>
          <p className={`text-base text-[#D6D0C5] leading-relaxed ${WIDE ? "lg:text-lg lg:max-w-xl" : ""}`}>{r.intro}</p>
          {onPromo && (
            <div className="mt-5 space-y-3" data-testid="reading-promo-badge">
              <p className="inline-block border border-[#D4AF37]/50 rounded-full px-4 py-1.5 text-sm gold-text">Free for a limited time with Astra Premium{onPromo.extended ? ", extended!" : ""}</p>
              <div><PromoCountdown endsAt={onPromo.ends_at} /></div>
            </div>
          )}
        </div>
        {WIDE && <div className="hidden lg:block"><PhoneFrame testId="hero-sample">{r.slug === "synastry" ? <div className="px-4 pt-2 scale-[0.92] origin-top"><SynastrySnapshot report={SAMPLE_SYNASTRY} /></div> : <SampleScreen sample={r.sample} />}</PhoneFrame></div>}
      </div>

      <Block kicker="Inside the reading" title="What's included" testId="reading-includes">
        <ul className={`grid gap-3 ${WIDE ? "lg:grid-cols-2 lg:gap-x-10" : ""}`}>
          {r.includes.map((x) => <li key={x} className="flex gap-3 text-base text-[#D6D0C5]"><Check size={18} className="gold-text shrink-0 mt-0.5" />{x}</li>)}
        </ul>
      </Block>

      <Block kicker="Sample" title="What your report looks like" testId="reading-samples">
        <Samples r={r} />
        <p className="text-xs text-[#A7B0C0] mt-2">Sample with made-up names. Yours is written from {["synastry", "composite"].includes(r.slug) || r.needs === "partner" ? "both of your real birth charts" : "your own real birth chart"}.</p>
      </Block>

      <Block kicker="In real life" title="How people use it" testId="reading-uses">
        <ul className={`grid gap-3 ${WIDE ? "lg:grid-cols-2 lg:gap-5" : ""}`}>
          {r.uses.map((u) => <li key={u} className="bg-[#0F1523]/90 border border-[#D4AF37]/15 rounded-2xl p-4 text-base text-[#D6D0C5]">{u}</li>)}
        </ul>
      </Block>

      <Stats r={r} />
      <Privacy />

      {!isNative && (
        <Block kicker="Ready when you are" title={`Get Your ${r.title} Today`} testId="reading-cta">
          {slug === "synastry" ? <SynastryOffer promo={onPromo} /> : onPromo ? <PromoOffer promo={onPromo} r={r} /> : <BuyBlock r={r} />}
        </Block>
      )}
    </PublicShell>
  );
}
